<template>
    <div>
        <headline-container headline="Create Competition" :breadcrumbs="breadcrumbs"></headline-container>

        <div class="container" v-if="!loading">
            <form @submit.prevent="submit">
                <div class="row">
                    <div class="col-xs-6">
                        <help-text>
                            <h3>
                                Create Competition
                            </h3>

                            <p>
                                Enter the details of the competition.
                            </p>
                        </help-text>
                    </div>

                    <div class="col-xs-18">
                        <div class="panel panel-default">
                            <div class="panel-body">
                                <text-form-group
                                    v-model="data.name"
                                    name="name"
                                    label="Name"
                                    help-text="The name of the competition."
                                    :error="getError('name')"
                                ></text-form-group>

                                <ck-editor-form-group
                                    v-model="data.description"
                                    name="description"
                                    label="Description"
                                    help-text="The description of the competition."
                                    :error="getError('description')"
                                ></ck-editor-form-group>

                                <text-form-group
                                    v-model="data.shared_target"
                                    name="shared_target"
                                    label="Shared Target"
                                    help-text="A shared target that all participants in the competition contribute to."
                                    :error="getError('shared_target')"
                                ></text-form-group>

                                <date-form-group
                                    v-model="data.starts_at"
                                    name="starts_at"
                                    label="Starts At"
                                    help-text="The start date of the competition."
                                    :error="getError('starts_at')"
                                ></date-form-group>

                                <date-form-group
                                    v-model="data.ends_at"
                                    name="ends_at"
                                    label="Ends At"
                                    help-text="The end date of the competition."
                                    :error="getError('ends_at')"
                                ></date-form-group>

                                <RepeatableFormGroup
                                    label="Prizes"
                                    v-model="data.prizes"
                                    help-text="The prizes that are awarded for the competition."
                                    :errors="prizeErrors"
                                >
                                    <template v-slot:default="{ data, index, change, errors }">
                                        <input
                                            type="text"
                                            class="form-control"
                                            :value="data.text"
                                            @input="change(index, 'text', $event.target.value)"
                                        >
                                    </template>
                                </RepeatableFormGroup>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="panel-separator">

                <div class="row">
                    <div class="col-xs-6">
                        <help-text>
                            <h3>
                                Competition Driver
                            </h3>

                            <p>
                                Select the type of the competition.
                            </p>
                        </help-text>
                    </div>

                    <div class="col-xs-18">
                        <div class="panel panel-default">
                            <div class="panel-body">
                                <select-form-group
                                    v-model="data.driver"
                                    name="driver"
                                    label="Driver"
                                    help-text="The type of the competition."
                                    :error="getError('driver')"
                                    :options="[{value: 'advertising-sales', label: 'Advertising Sales'}, {value: 'advertising-stars', label: 'Advertising Stars'}, {value: 'partnership-sales', label: 'Partnership Sales'}, {value: 'partnership-stars', label: 'Partnership Stars'}, {value: 'sales', label: 'Sales'}, {value: 'stars', label: 'Stars'}]"
                                ></select-form-group>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="panel-separator">

                <div class="row">
                    <div class="col-xs-6">
                        <help-text>
                            <h3>
                                Driver Settings
                            </h3>

                            <p>
                                Adjust the driver specific settings for the competition.
                            </p>
                        </help-text>
                    </div>

                    <div class="col-xs-18">
                        <div class="panel panel-default">
                            <div class="panel-body">
                                <base-form
                                    v-if="data.driver"
                                    v-model="data.driver_parameters"
                                    :driver="data.driver"
                                    :errors="driverErrors"
                                />

                                <div v-else>
                                    <i>
                                        Select a driver to display the settings.
                                    </i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-offset-6 col-xs-18">
                        <button type="submit" class="btn btn-block btn-black tw-mb-8">
                            <i class="fa fa-save"></i> Save
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import BaseForm from '@/components/Administration/Competitions/DriverParameterForms/Base'
import CkEditorFormGroup from '@/vendor/forms/FormGroups/Block/CkEditor'
import DateFormGroup from '@/vendor/forms/FormGroups/Block/Date'
import Errors from '@/mixins/Errors'
import FormGroup from '@/vendor/forms/FormGroups/Block/FormGroup'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import HelpText from '@/components/HelpText'
import SelectFormGroup from '@/vendor/forms/FormGroups/Block/Select'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Block/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Block/Text'
import RepeatableFormGroup from '../../../components/Forms/RepeatableFormGroup'

export default {
    components: {
        RepeatableFormGroup,
        BaseForm,
        CkEditorFormGroup,
        DateFormGroup,
        FormGroup,
        HeadlineContainer,
        HelpText,
        SelectFormGroup,
        SelectWidget,
        SubmitFormGroup,
        TextFormGroup
    },

    mixins: [Errors],

    data() {
        return {
            data: {
                driver_parameters: {},
                prizes: []
            },

            subscriptionTypes: null,

            loading: true
        }
    },

    mounted() {
        this.load()
    },

    watch: {
        'data.driver'(value) {
            if (value === 'advertising-sales') {
                this.data.driver_parameters = {ad_formats: []}
            }

            if (value === 'partnership-sales') {
                this.data.driver_parameters = {subscription_types: []}
            }

            if (value === 'sales') {
                this.data.driver_parameters = {}
            }

            if (value === 'stars') {
                this.data.driver_parameters = {}
            }
        }
    },

    computed: {
        breadcrumbs() {
            return [
                {url: '/competitions', text: 'Competitions'},
                {text: 'Create'}
            ]
        },

        driverErrors() {
            return Object.entries(this.errors)
                .filter(entry => entry[0].startsWith('driver_parameters.'))
                .reduce((result, entry) => ({
                    ...result,
                    [entry[0].substring('driver_parameters.'.length)]: entry[1]
                }), {})
        },

        prizeErrors() {
            return Object.entries(this.errors)
                .filter(entry => entry[0].startsWith('prizes.'))
                .reduce((result, entry) => ({
                    ...result,
                    [entry[0].substring('prizes.'.length)]: entry[1]
                }), {})
        }
    },

    methods: {
        load() {
            http.get('/api/v2/subscription-types').then(response => {
                this.subscriptionTypes = response.data.data

                this.loading = false
            })
        },

        submit() {
            http.post('/api/v2/competitions', this.data)
                .then(() => {
                    notify.success('The competition was saved')

                    this.$router.push('/competitions')
                })
                .catch(this.catchValidationErrors)
        }
    }
}
</script>
